import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Eye, EyeOff, Mail, Lock, User, Building2, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerEnteName, setRegisterEnteName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Email verification states
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      if (user.role === 'platform_admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  // Check if coming from expired verification link
  useEffect(() => {
    if (searchParams.get('resend') === 'true') {
      setEmailNotVerified(true);
    }
  }, [searchParams]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setEmailNotVerified(false);
    
    try {
      const userData = await login(loginEmail, loginPassword);
      if (userData.role === 'platform_admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      // Check if email not verified
      if (error.response?.data?.detail === 'EMAIL_NOT_VERIFIED') {
        setEmailNotVerified(true);
        setRegisteredEmail(loginEmail);
        toast.error('Il tuo indirizzo email non è ancora verificato.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await register(registerEmail, registerPassword, registerName, registerEnteName);
      
      if (result.requires_verification) {
        setShowVerificationMessage(true);
        setRegisteredEmail(registerEmail);
        toast.success('Registrazione completata! Controlla la tua email.');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      // Error handled in auth context
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const emailToResend = registeredEmail || loginEmail;
    if (!emailToResend) {
      toast.error('Inserisci la tua email');
      return;
    }
    
    setResendingEmail(true);
    try {
      await axios.post(`${API}/api/auth/resend-verification`, { email: emailToResend });
      toast.success('Nuovo link di verifica inviato! Controlla la tua email.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.includes('2 minuti')) {
        toast.error('Attendi 2 minuti prima di richiedere un nuovo link.');
      } else if (detail?.includes('già verificata')) {
        toast.info('Email già verificata. Puoi effettuare il login.');
        setEmailNotVerified(false);
      } else {
        toast.error(detail || 'Errore durante l\'invio');
      }
    } finally {
      setResendingEmail(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Show verification success message after registration
  if (showVerificationMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Controlla la tua email</CardTitle>
            <CardDescription className="text-base mt-2">
              Abbiamo inviato un link di verifica a:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-100 rounded-lg p-3 text-center">
              <span className="font-medium text-slate-700">{registeredEmail}</span>
            </div>
            <p className="text-sm text-slate-600 text-center">
              Clicca sul link nell'email per attivare il tuo account. Il link è valido per 24 ore.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Non trovi l'email?</strong> Controlla la cartella spam o posta indesiderata.
            </div>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={handleResendVerification}
              disabled={resendingEmail}
            >
              {resendingEmail ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Invio in corso...</>
              ) : (
                <><Mail className="w-4 h-4 mr-2" /> Invia nuovamente email</>
              )}
            </Button>
            <Button 
              variant="ghost" 
              className="w-full"
              onClick={() => {
                setShowVerificationMessage(false);
                setRegisterEmail('');
                setRegisterPassword('');
                setRegisterName('');
                setRegisterEnteName('');
              }}
            >
              Torna al login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1743793174491-bcbdf1882ad5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwdm9sdW50ZWVycyUyMHdvcmtpbmclMjB0b2dldGhlciUyMGdyb3VwJTIwc21pbGluZ3xlbnwwfHx8fDE3NzI5NTgzODJ8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-[#0055BB]/80" />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <h1 className="text-4xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Assietica Gest
          </h1>
          <p className="text-sm opacity-80 mb-4 italic">by Assietica</p>
          <p className="text-xl opacity-90 mb-8">
            Il gestionale semplice e sicuro per la tua associazione
          </p>
          <ul className="space-y-3 text-lg opacity-80">
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-white rounded-full" />
              Gestione completa dei volontari
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-white rounded-full" />
              Organizzazione eventi e presenze
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-white rounded-full" />
              Tracciabilità assicurativa
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-white rounded-full" />
              Registro volontari stampabile
            </li>
          </ul>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 lg:hidden">
            <h1 className="text-3xl font-bold text-[#0055BB]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Assietica Gest
            </h1>
            <p className="text-sm text-slate-500 italic">by Assietica</p>
            <p className="text-slate-500 mt-2">Il gestionale semplice e sicuro per la tua associazione</p>
          </div>

          <Card className="shadow-lg">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl text-center">Benvenuto</CardTitle>
              <CardDescription className="text-center">
                Accedi o registra la tua associazione
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="tab-login">Accedi</TabsTrigger>
                  <TabsTrigger value="register" data-testid="tab-register">Registrati</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  {/* Email not verified warning */}
                  {emailNotVerified && (
                    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-amber-800">
                            Email non verificata
                          </p>
                          <p className="text-sm text-amber-700 mt-1">
                            Il tuo indirizzo email non è ancora verificato. Controlla la tua casella di posta o richiedi un nuovo link.
                          </p>
                          <Button 
                            type="button"
                            variant="outline" 
                            size="sm"
                            className="mt-2 border-amber-300 text-amber-700 hover:bg-amber-100"
                            onClick={handleResendVerification}
                            disabled={resendingEmail}
                          >
                            {resendingEmail ? (
                              <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Invio...</>
                            ) : (
                              <><Mail className="w-3 h-3 mr-1" /> Invia nuovo link</>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="email@esempio.it"
                          className="pl-10"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          required
                          data-testid="login-email"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                        <Input
                          id="login-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="••••••••"
                          className="pl-10 pr-10"
                          value={loginPassword}
                          onChange={(e) => setLoginPassword(e.target.value)}
                          required
                          data-testid="login-password"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full btn-primary" 
                      disabled={loading}
                      data-testid="login-submit"
                    >
                      {loading ? <span className="spinner mr-2" /> : null}
                      Accedi
                    </Button>
                    
                    <div className="text-center">
                      <Link 
                        to="/reset-password" 
                        className="text-sm text-[#0055BB] hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Password dimenticata?
                      </Link>
                    </div>
                  </form>

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-slate-500">oppure</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleGoogleLogin}
                    data-testid="google-login"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Continua con Google
                  </Button>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Nome e Cognome</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="Mario Rossi"
                          className="pl-10"
                          value={registerName}
                          onChange={(e) => setRegisterName(e.target.value)}
                          required
                          data-testid="register-name"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-ente">Nome Associazione</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-ente"
                          type="text"
                          placeholder="Associazione Volontari XYZ"
                          className="pl-10"
                          value={registerEnteName}
                          onChange={(e) => setRegisterEnteName(e.target.value)}
                          required
                          data-testid="register-ente"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="email@esempio.it"
                          className="pl-10"
                          value={registerEmail}
                          onChange={(e) => setRegisterEmail(e.target.value)}
                          required
                          data-testid="register-email"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Minimo 8 caratteri"
                          className="pl-10 pr-10"
                          value={registerPassword}
                          onChange={(e) => setRegisterPassword(e.target.value)}
                          required
                          minLength={8}
                          data-testid="register-password"
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full btn-primary" 
                      disabled={loading}
                      data-testid="register-submit"
                    >
                      {loading ? <span className="spinner mr-2" /> : null}
                      Registra Associazione
                    </Button>
                  </form>

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-slate-500">oppure</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={handleGoogleLogin}
                    data-testid="google-register"
                  >
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Registrati con Google
                  </Button>
                </TabsContent>
              </Tabs>

              <p className="text-xs text-slate-400 text-center mt-6">
                Continuando, accetti i nostri Termini di Servizio e la Privacy Policy.
              </p>
              
              <div className="text-center mt-8 pt-6 border-t border-slate-100">
                <p className="text-xs text-slate-500">
                  © 2026 Assietica Società Cooperativa – Società Benefit
                </p>
                <p className="text-xs text-slate-500">
                  Tutti i diritti riservati.
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Ideazione e sviluppo: Mauro Antonio Mura
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
